import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import debounce from 'lodash/debounce';
import { confirmAlert } from 'react-confirm-alert';
import { useTranslation } from 'react-i18next';
import { useRootStore } from '../../../../mobx/bridge';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';
import styles from './ContactInfoPhoneNumber.module.css';
import * as utils from '../../../../helpers/utils';

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const ContactInfoPhoneNumber = observer(function ContactInfoPhoneNumber(props) {
  const { commonStore, channelTypeStore, sendingEvents, settingsStore } = useRootStore();
  const { t } = useTranslation();

  const { phone, contactToEdit, isWebChatChannel } = props;

  const settings = settingsStore.getLoggedUserSettings;

  const openChannel = debounce(() => {
    if (settings?.SelectedChannel?.CustomerPhoneNumber?._id !== phone._id) {
      const channelData = {
        _id: utils.generateGuid(),
        IsPublic: true,
        ChannelName: t('Common.TextMessageWithContact', { contactName: contactToEdit.getFullName }),
        ChannelDescription: null,
        ChannelType: channelTypeStore.computedGetByTypeId(utils.ChannelTypeEnum.SMS.TypeId),
        Contacts: [commonStore.getLoggedUser, contactToEdit],
        CustomerPhoneNumber: phone,
        IsDeleted: false,
      };

      sendingEvents.socketEmitRoom(
        channelData,
        utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
      );
    }
  }, 250);

  const blockPhoneNumber = () => {
    sendingEvents.socketEmitBlockPhoneNumber(phone);
  };

  const confirmBlockPhoneNumber = (e) => {
    e?.stopPropagation();

    confirmAlert({
      customUI: ({ onClose }) => (
        <CustomConfirmAlert
          title={t('ContactInfoPhoneNumber.YouAreAboutToBlockANumberTitle')}
          message={t('ContactInfoPhoneNumber.ConfirmBlockPhoneNumberMessage', {
            phoneNumber: phone.Number,
          })}
          trueText={t('Common.Confirm')}
          trueEvent={blockPhoneNumber}
          falseText={t('Common.Close')}
          falseEvent={onClose}
        />
      ),
    });
  };

  const unblockPhoneNumber = (e) => {
    e?.stopPropagation();

    sendingEvents.socketEmitUnblockPhoneNumber(phone);
  };

  const renderToggleBlockPhoneNumber = () => {
    if (!isWebChatChannel) {
      return !phone.IsBlocked ? (
        <React.Fragment>
          <span className={styles.blockedNumberIcon} />

          <button type="button" className={styles.blockNumberBtn} onClick={confirmBlockPhoneNumber}>
            {t('ContactInfoPhoneNumber.Block')}
          </button>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <span className={[styles.blockedNumberIcon, styles.displayElement].join(' ')} />

          <button
            type="button"
            className={[styles.blockNumberBtn, styles.unblockNumberBtn, styles.displayElement].join(
              ' '
            )}
            onClick={unblockPhoneNumber}
          >
            {t('ContactInfoPhoneNumber.Unblock')}
          </button>
        </React.Fragment>
      );
    }

    return null;
  };

  const containerStyles = [styles.itemContainer];
  if (contactToEdit.PhoneNumbers?.length > 1) {
    containerStyles.push(styles.selectable);
  }

  if (isWebChatChannel) {
    containerStyles.push(styles.itemContainerWebChat);
  }

  return (
    <React.Fragment>
      <Grid component="div" container key={`p-${phone._id}`} className={containerStyles.join(' ')}>
        <Grid component="div" container item xs align="left" onClick={openChannel}>
          <Grid
            component="div"
            container
            item
            xs={12}
            className={styles.overflowFix}
            alignContent="center"
            alignItems="center"
          >
            <span
              className={
                !isWebChatChannel
                  ? styles.phoneNumber
                  : [styles.phoneNumber, styles.phoneNumberWebChat].join(' ')
              }
              data-testid="description"
            >
              {phone.Number}
            </span>

            {renderToggleBlockPhoneNumber()}

            {phone.Description ? (
              <div className={styles.descriptionContainer}>
                <span className={styles.description} data-testid="description">
                  {phone.Description}
                </span>
              </div>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
});
ContactInfoPhoneNumber.whyDidYouRender = true;
ContactInfoPhoneNumber.displayName = 'ContactInfoPhoneNumber';

export default ContactInfoPhoneNumber;
